import PropTypes from 'prop-types'
import {
  Box,
  Button,
  Tag,
  TagLabel,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react'
import {useTranslation} from 'react-i18next'
import {toast} from 'react-toastify'

import {NewSupplierLayout as SupplierLayout} from '../../layouts/SupplierLayout'
import {AuthProvider} from '../../contexts/authentication'
import withProviders from '../../contexts/withProviders'
import EmptyState from '../../components/EmptyState'
import ResponsiveDataTable from '../../components/ResponsiveDataTable'
import {NextScreenHeader as ScreenHeader} from '../../components/ScreenHeader'
import {switchAccount} from '../../../api'

const accountShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  isCurrent: PropTypes.bool,
  // False when the account has no active supplier to land on, so no switch
  // action is offered.
  switchable: PropTypes.bool,
})

const StatusCell = ({row, t}) => {
  const {status} = row.original
  return (
    <Tag
      variant="status"
      colorScheme={status === 'active' ? 'success' : 'neutral'}
    >
      <TagLabel>{t(`accountsPage:status.${status}`)}</TagLabel>
    </Tag>
  )
}

StatusCell.propTypes = {
  row: PropTypes.shape({original: accountShape.isRequired}).isRequired,
  t: PropTypes.func.isRequired,
}

const SwitchCell = ({row, onSwitch, t}) => {
  const account = row.original
  if (account.isCurrent) {
    return (
      <Text textStyle="body-small" color="mld.neutral.700">
        {t('accountsPage:currentLabel')}
      </Text>
    )
  }
  if (!account.switchable) return null
  return (
    <Button variant="secondary" size="sm" onClick={() => onSwitch(account.id)}>
      {t('accountsPage:switchCta')}
    </Button>
  )
}

SwitchCell.propTypes = {
  row: PropTypes.shape({original: accountShape.isRequired}).isRequired,
  onSwitch: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
}

const AccountsScreen = withProviders(({authenticityToken, accounts}) => {
  const {t} = useTranslation('accountsPage')

  const handleSwitch = (accountId) => {
    switchAccount({authenticityToken, accountId})
      .then(() => window.location.assign('/dashboard'))
      .catch(() => toast.error(t('accountsPage:switchError')))
  }

  // On small screens the status wraps onto its own line under the name
  // (ResponsiveDataTable's `wrap`), leaving the main row to the name and the
  // switch action; wrapped columns drop their header.
  const isMobile = useBreakpointValue({base: true, md: false})

  const nameColumn = {
    header: t('accountsPage:table.nameHeader'),
    accessorKey: 'name',
    colSpan: isMobile ? 2 : 3,
  }
  const statusColumn = {
    header: t('accountsPage:table.statusHeader'),
    id: 'status',
    colSpan: 1,
    wrap: isMobile,
    cell: ({row}) => <StatusCell row={row} t={t} />,
  }
  const actionsColumn = {
    header: '',
    id: 'actions',
    colSpan: 1,
    cell: ({row}) => <SwitchCell row={row} onSwitch={handleSwitch} t={t} />,
  }

  const columns = isMobile
    ? [nameColumn, actionsColumn, statusColumn]
    : [nameColumn, statusColumn, actionsColumn]

  return (
    <AuthProvider authenticityToken={authenticityToken}>
      <SupplierLayout state="Accounts" variant="secondary">
        <ScreenHeader text={t('accountsPage:title')} />
        <Box
          mt="md"
          p="md"
          borderRadius="lg"
          background="mld.neutral.0"
          overflowX="auto"
        >
          {accounts.length === 0 ? (
            <EmptyState
              heading={t('accountsPage:emptyState.heading')}
              subHeading={t('accountsPage:emptyState.subHeading')}
            />
          ) : (
            <ResponsiveDataTable columns={columns} data={accounts} />
          )}
        </Box>
      </SupplierLayout>
    </AuthProvider>
  )
})

AccountsScreen.propTypes = {
  authenticityToken: PropTypes.string.isRequired,
  accounts: PropTypes.arrayOf(accountShape).isRequired,
}

export default AccountsScreen
